import React from "react";
import getAxios from "../../hooks/useAxios";
import Cookies from "js-cookie";
import {
  Box,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Flex,
  Spinner,
  Badge,
  Icon,
} from "@chakra-ui/react";
import { useTheme } from "@chakra-ui/react";
import { Theme } from "../../theme";
import { MdOutlineModeEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const RideFare = () => {
  const axios = getAxios();
  const navigate = useNavigate();

  const access_token = Cookies.get("x-admin-auth");
  const theme = useTheme<Theme>();

  const [rideFare, setRideFare] = React.useState<any>();
  const [isDataFetching, setIsDataFetching] = React.useState(true);

  const getRideFare = async () => {
    try {
      const res = await axios.get(`/pricing-config`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      console.log("res ", res.data);
      setRideFare(res.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsDataFetching(false);
    }
  };

  React.useEffect(() => {
    getRideFare();
  }, []);

  const formatTime = (time?: string) => {
    if (!time) return "-";

    const [hours, minutes] = time.split(":");
    const date = new Date();

    date.setHours(Number(hours), Number(minutes));

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (date?: string) => {
    if (!date) return "-";

    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEditButtonClick = () => {
    navigate({
      pathname: "/ride-fare/edit",
    });
  };

  const FareItem = ({
    label,
    value,
    suffix,
  }: {
    label: string;
    value: any;
    suffix?: string;
  }) => {
    return (
      <Box
        p="1rem"
        borderRadius="10px"
        bg="gray.50"
        border="1px solid"
        borderColor="gray.200"
      >
        <Text fontSize="0.85rem" color="gray.500" mb="0.4rem">
          {label}
        </Text>

        <Flex alignItems="baseline" gap="0.3rem">
          <Text fontSize="1.25rem" fontWeight="600" color="gray.800">
            {value ?? "-"}
          </Text>

          {suffix && (
            <Text fontSize="0.8rem" color="gray.500">
              {suffix}
            </Text>
          )}
        </Flex>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        padding: "0 1rem",
        overflowY: "auto",
      }}
    >
      <Flex gap={"1rem"} alignItems="center">
        <Text
          sx={{
            fontSize: theme.fontSizes.subHeading1,
            margin: "2.5rem 0 2rem 2rem",
            fontWeight: "600",
          }}
        >
          Ride Fare
        </Text>

        <Icon
          as={MdOutlineModeEdit}
          width={5}
          height={5}
          color={theme.colors.primary.main}
          cursor="pointer"
          onClick={() => handleEditButtonClick()}
        />
      </Flex>

      {isDataFetching ? (
        <Flex justifyContent="center" alignItems="center" height="300px">
          <Spinner size="md" />
        </Flex>
      ) : !rideFare ? (
        <Flex justifyContent="center" alignItems="center" height="300px">
          <Text color="gray.500">No pricing configuration found.</Text>
        </Flex>
      ) : (
        <Box maxWidth="1100px" margin="2rem" paddingBottom="2rem">
          {/* Base Fare */}
          <Card
            borderRadius="12px"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            mb="1.5rem"
          >
            <CardBody p="1.5rem">
              <Flex
                justifyContent="space-between"
                alignItems="center"
                mb="1rem"
              >
                <Text fontSize="1rem" fontWeight="600" color="gray.800">
                  Base Fare
                </Text>
              </Flex>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="1rem">
                <FareItem
                  label="Base Fare"
                  value={`₹${rideFare.base_fare}`}
                  suffix="starting fare"
                />

                <FareItem
                  label="Pickup Rate"
                  value={`₹${rideFare.pickup_rate_per_km}`}
                  suffix="per km"
                />
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Distance Pricing */}
          <Card
            borderRadius="12px"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            mb="1.5rem"
          >
            <CardBody p="1.5rem">
              <Text fontSize="1rem" fontWeight="600" color="gray.800" mb="1rem">
                Distance Pricing
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="1rem">
                <FareItem
                  label="Day Rate"
                  value={`₹${rideFare.day_rate_per_km}`}
                  suffix="per km"
                />

                <FareItem
                  label="Night Rate"
                  value={`₹${rideFare.night_rate_per_km}`}
                  suffix="per km"
                />
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Night Timing */}
          <Card
            borderRadius="12px"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            mb="1.5rem"
          >
            <CardBody p="1.5rem">
              <Text fontSize="1rem" fontWeight="600" color="gray.800" mb="1rem">
                Night Timing
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="1rem">
                <FareItem
                  label="Night Starts"
                  value={formatTime(rideFare.night_start_time)}
                />

                <FareItem
                  label="Night Ends"
                  value={formatTime(rideFare.night_end_time)}
                />
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Slab Multipliers */}
          <Card
            borderRadius="12px"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
            mb="1.5rem"
          >
            <CardBody p="1.5rem">
              <Text fontSize="1rem" fontWeight="600" color="gray.800" mb="1rem">
                Slab Multipliers (Discount)
              </Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="1rem">
                <FareItem
                  label="Slab 1 Multiplier (Discount)"
                  value={rideFare.slab1_multiplier}
                />

                <FareItem
                  label="Slab 2 Multiplier (Discount)"
                  value={rideFare.slab2_multiplier}
                />
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* Updated Information */}
          <Card
            borderRadius="12px"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="sm"
          >
            <CardBody p="1.5rem">
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing="1.5rem">
                <Box>
                  <Text fontSize="0.8rem" color="gray.500" mb="0.3rem">
                    Last Updated
                  </Text>

                  <Text fontSize="0.95rem" fontWeight="500" color="gray.800">
                    {formatDate(rideFare.updated_at)}
                  </Text>
                </Box>

                <Box>
                  <Text fontSize="0.8rem" color="gray.500" mb="0.3rem">
                    Configuration ID
                  </Text>

                  <Text
                    fontSize="0.85rem"
                    fontWeight="500"
                    color="gray.700"
                    wordBreak="break-all"
                  >
                    {rideFare.id}
                  </Text>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default RideFare;
